import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  CalendarDays,
  Users,
  CheckCircle2,
  Plane,
  BriefcaseBusiness,
  Info,
} from 'lucide-react';

interface VacancesDisponibilitesSAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const teamVacations = [
  {
    initials: 'EH',
    person: 'Eric Hains',
    periods: ['9 et 10 juillet 2026', '13 au 17 juillet 2026'],
    color: 'bg-orange-500',
    lightColor: 'bg-orange-100 border-orange-200 text-orange-900',
    bar: [
      '9-10 juillet',
      '13-17 juillet',
      '',
      '',
      '',
      '',
    ],
  },
  {
    initials: 'PB',
    person: 'Philippe Bolduc',
    periods: ['13 au 17 juillet 2026'],
    color: 'bg-blue-500',
    lightColor: 'bg-blue-100 border-blue-200 text-blue-900',
    bar: [
      '',
      '13-17 juillet',
      '',
      '',
      '',
      '',
    ],
  },
  {
    initials: 'JMC',
    person: 'Jean-Michel Caouette',
    periods: ['20 au 24 juillet 2026', '27 au 31 juillet 2026'],
    color: 'bg-fuchsia-500',
    lightColor: 'bg-fuchsia-100 border-fuchsia-200 text-fuchsia-900',
    bar: [
      '',
      '',
      '20-24 juillet',
      '27-31 juillet',
      '',
      '',
    ],
  },
  {
    initials: 'MDB',
    person: 'Mikael Drouin-Bouchard',
    periods: ['20 au 24 juillet 2026', '27 au 31 juillet 2026'],
    color: 'bg-purple-500',
    lightColor: 'bg-purple-100 border-purple-200 text-purple-900',
    bar: [
      '',
      '',
      '20-24 juillet',
      '27-31 juillet',
      '',
      '',
    ],
  },
  {
    initials: 'FM',
    person: 'Francis Marquis',
    periods: ['3 au 7 août 2026', '10 au 14 août 2026'],
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-100 border-emerald-200 text-emerald-900',
    bar: [
      '',
      '',
      '',
      '',
      '3-7 août',
      '10-14 août',
    ],
  },
];

const weekColumns = [
  '6-10 juillet',
  '13-17 juillet',
  '20-24 juillet',
  '27-31 juillet',
  '3-7 août',
  '10-14 août',
];

const highImpactWeeks = [
  {
    period: '13 au 17 juillet',
    absent: 'EH et PB absents',
    note: 'Deux membres SA absents la même semaine. À considérer pour les suivis et rencontres planifiées.',
  },
  {
    period: '20 au 31 juillet',
    absent: 'JMC et MDB absents',
    note: 'Deux semaines complètes où JMC et MDB sont absents. Prévoir la relève pour leurs dossiers actifs.',
  },
  {
    period: '3 au 14 août',
    absent: 'FM absent',
    note: 'Planifier les validations de direction SA avant ou après cette période.',
  },
];

function SectionCard({
  title,
  icon: Icon,
  children,
  accent = 'cyan',
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  accent?: 'cyan' | 'emerald' | 'amber' | 'violet';
}) {
  const styles = {
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-800',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    violet: 'border-violet-200 bg-violet-50 text-violet-800',
  }[accent];

  return (
    <div className={`rounded-2xl border p-5 ${styles}`}>
      <h4 className="mb-3 flex items-center gap-2 text-lg font-black text-slate-900">
        <Icon className="h-5 w-5" />
        {title}
      </h4>
      <div className="space-y-3 text-sm leading-relaxed text-slate-700">{children}</div>
    </div>
  );
}

export default function VacancesDisponibilitesSAModal({ isOpen, onClose }: VacancesDisponibilitesSAModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 8 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[92vh] w-full max-w-7xl overflow-y-auto rounded-3xl border border-cyan-200 bg-white shadow-2xl"
          >
            <div className="sticky top-0 z-20 flex items-start justify-between gap-4 border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur">
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.25em] text-cyan-700">Solutions d’affaires</p>
                <h2 className="text-3xl font-black text-slate-950">Vacances SA — Été 2026</h2>
                <p className="mt-1 max-w-4xl text-sm text-slate-500">
                  Calendrier consolidé des vacances confirmées de l’équipe Solutions d’affaires pour planifier les rencontres, projets et suivis clients.
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                type="button"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6 bg-gradient-to-br from-slate-50 to-cyan-50/40 p-6">
              <div className="rounded-3xl border border-cyan-200 bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-800 p-7 text-white shadow-lg">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="mb-2 text-xs font-bold uppercase tracking-[0.28em] text-cyan-200">Calendrier d’équipe confirmé</p>
                    <h3 className="max-w-4xl text-3xl font-black leading-tight md:text-4xl">
                      Vue rapide des absences SA — juillet et août 2026
                    </h3>
                    <p className="mt-4 max-w-4xl text-base leading-relaxed text-cyan-50/90">
                      Cette vue permet de voir rapidement les chevauchements de vacances et d’éviter de planifier des livraisons,
                      validations ou rencontres importantes lorsque plusieurs membres de l’équipe sont absents.
                    </p>
                  </div>
                  <div className="grid min-w-[300px] grid-cols-3 gap-3 text-center">
                    <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
                      <p className="text-3xl font-black">5</p>
                      <p className="text-xs font-semibold text-cyan-100">personnes confirmées</p>
                    </div>
                    <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
                      <p className="text-3xl font-black">6</p>
                      <p className="text-xs font-semibold text-cyan-100">semaines couvertes</p>
                    </div>
                    <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
                      <p className="text-3xl font-black">0</p>
                      <p className="text-xs font-semibold text-cyan-100">à confirmer</p>
                    </div>
                  </div>
                </div>
              </div>

              <SectionCard title="Calendrier visuel" icon={CalendarDays} accent="cyan">
                <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="min-w-[980px]">
                    <div className="grid grid-cols-[180px_repeat(6,1fr)] border-b border-slate-200 bg-slate-900 text-white">
                      <div className="px-4 py-3 text-sm font-black">Membre</div>
                      {weekColumns.map((week) => (
                        <div key={week} className="border-l border-white/10 px-3 py-3 text-center text-sm font-black">
                          {week}
                        </div>
                      ))}
                    </div>

                    {teamVacations.map((member) => (
                      <div key={member.initials} className="grid grid-cols-[180px_repeat(6,1fr)] border-b border-slate-100 last:border-b-0">
                        <div className="flex items-center gap-3 bg-slate-50 px-4 py-3">
                          <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${member.color} text-sm font-black text-white shadow-sm`}>
                            {member.initials}
                          </span>
                          <div>
                            <p className="text-sm font-black text-slate-950">{member.person}</p>
                            <p className="text-xs text-slate-500">{member.initials}</p>
                          </div>
                        </div>
                        {member.bar.map((value, index) => (
                          <div key={`${member.initials}-${weekColumns[index]}`} className="min-h-[64px] border-l border-slate-100 p-2">
                            {value ? (
                              <div className={`flex h-full min-h-[46px] items-center justify-center rounded-xl px-2 text-center text-xs font-black text-white shadow-sm ${member.color}`}>
                                {value}
                              </div>
                            ) : (
                              <div className="h-full min-h-[46px] rounded-xl bg-slate-50" />
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Note : les périodes qui commencent ou se terminent en milieu de semaine sont indiquées dans la case correspondante.
                </p>
              </SectionCard>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                <SectionCard title="Objectif" icon={BriefcaseBusiness} accent="cyan">
                  <p>
                    Donner une vue claire et officielle des vacances SA pour mieux planifier les projets, rencontres clients et validations internes.
                  </p>
                </SectionCard>
                <SectionCard title="Statut" icon={CheckCircle2} accent="emerald">
                  <p>
                    Toutes les vacances listées dans cette card sont confirmées pour l’été 2026.
                  </p>
                </SectionCard>
                <SectionCard title="Utilisation" icon={Info} accent="amber">
                  <p>
                    Consulter cette vue avant de fixer une rencontre importante ou d’assigner une livraison qui dépend d’un membre précis.
                  </p>
                </SectionCard>
              </div>

              <SectionCard title="Tableau détaillé par personne" icon={Users} accent="emerald">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {teamVacations.map((member) => (
                    <div key={member.person} className={`rounded-2xl border p-5 shadow-sm ${member.lightColor}`}>
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <p className="text-lg font-black text-slate-950">{member.person}</p>
                          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{member.initials}</p>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/75 px-2.5 py-1 text-xs font-bold text-slate-800">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
                          Confirmé
                        </span>
                      </div>
                      <div className="space-y-2">
                        {member.periods.map((period) => (
                          <div key={period} className="flex items-center gap-2 rounded-xl border border-white/70 bg-white/80 px-3 py-2 text-sm font-semibold text-slate-800">
                            <Plane className="h-4 w-4 text-cyan-700" />
                            {period}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Semaines à surveiller" icon={CalendarDays} accent="violet">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  {highImpactWeeks.map((week) => (
                    <div key={week.period} className="rounded-2xl border border-violet-100 bg-white p-5 shadow-sm">
                      <p className="text-base font-black text-slate-950">{week.period}</p>
                      <p className="mt-2 text-sm font-semibold text-violet-800">{week.absent}</p>
                      <p className="mt-3 text-xs leading-5 text-slate-500">{week.note}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
